import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useCallback, useState } from "react";
import { Form } from "antd";
import {
  StyledGridCol,
  StyledGridRow,
  StyledInformationList,
  StyledSectionHeading,
} from "@/common/styles";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import Button from "@/common/elements/Button";
import TextField from "@/common/elements/InputElements/TextField";
import { LeftOutlined } from "@ant-design/icons";
import StateSubmissions from "./StateSubmissions";
import { patchActiveWarrantArray, patchActiveWarrant } from "@/slices/warrantsSlice";
import { isNonEmptyVariable } from "@/util/common_function";
import Select from "@/common/elements/InputElements/Select";

const StateSubmissionInformation = ({ showSubMissions = false }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isShowSubMission, setIsShowSubMission] = useState(showSubMissions);
  const debounceRef = useRef(null);

  const { activeWarrant } = useSelector((s) => s?.warrants ?? {});
  const { bookingsFieldsEnabled, bookings } = useSelector((s) => s?.bookings);

  // warrantStateInfo[0] holds all state submission fields
  const stateInfo = activeWarrant?.warrantStateInfo?.[0] ?? {};

  // Subject name from bio
  const bio = bookings?.bio?.[0] ?? {};
  const useAlias = activeWarrant?.useAlias ?? false;
  const bioAliases = bio?.bioAliases ?? [];

  const aliasOptions = bioAliases.map((alias) => ({
    label: [alias?.lName, alias?.fName, alias?.mName, alias?.suffix]
      .filter(Boolean)
      .join(" "),
    value: alias?.bioAliasId,
  }));

  const name =
    isNonEmptyVariable(bio?.fName) || isNonEmptyVariable(bio?.lName)
      ? `${bio?.lName || ""}, ${bio?.fName || ""} ${
          isNonEmptyVariable(bio?.mName) ? bio?.mName : ""
        } ${isNonEmptyVariable(bio?.suffix) ? `, ${bio?.suffix}` : ""}`
          .replace(/\s+/g, " ")
          .trim()
      : "";

  const patchStateInfo = useCallback(
    (patch) => {
      const current = activeWarrant?.warrantStateInfo ?? [];
      const updated =
        current.length > 0
          ? [{ ...current[0], ...patch }, ...current.slice(1)]
          : [{ ...patch }];
      dispatch(patchActiveWarrantArray({ key: "warrantStateInfo", value: updated }));
    },
    [activeWarrant?.warrantStateInfo, dispatch]
  );

  // Debounced version for text fields — avoids Redux dispatch on every keystroke
  const patchStateInfoDebounced = useCallback(
    (patch) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        patchStateInfo(patch);
      }, 300);
    },
    [patchStateInfo]
  );

  // Only hydrate form when the warrant itself changes, not on every Redux patch.
  // This prevents the setFieldsValue → re-render loop that makes typing feel slow.
  const warrantId = activeWarrant?.warrantId;

  useEffect(() => {
    form.setFieldsValue({
      instructionMakeActiveAtState: stateInfo.instructionMakeActiveAtState ?? true,
      miscellaneousField: stateInfo.miscellaneousField ?? "",
      miscellaneousFieldAuto: stateInfo.miscellaneousFieldAuto ?? "",
      useAlias: activeWarrant?.useAlias ?? false,
      bioAliasId: activeWarrant?.bioAliasId ?? null,
    });

    if (!("instructionMakeActiveAtState" in stateInfo)) {
      patchStateInfo({ instructionMakeActiveAtState: true });
    }
  }, [warrantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <>
      <StyledSectionHeading style={{ marginBottom: "0" }}>
        <span>State Submission Information</span>
      </StyledSectionHeading>

      <StyledInformationList style={{ padding: "0" }}>
        <Form form={form} disabled={!bookingsFieldsEnabled}>
          <StyledGridRow gutter={16} justify="space-between">
            <StyledGridCol marginTop="10px">
              <FormItem
                label="Make active at State"
                layout="horizontal"
                labelPostion="label-right"
                name="instructionMakeActiveAtState"
                valuePropName="checked"
              >
                <Checkbox
                  onChange={(e) =>
                    patchStateInfo({ instructionMakeActiveAtState: e.target.checked })
                  }
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol marginTop="10px">
              <Button onClick={() => setIsShowSubMission((p) => !p)}>
                <span
                  style={{
                    transform: `rotate(${isShowSubMission ? "0deg" : "180deg"})`,
                  }}
                >
                  <LeftOutlined style={{ marginRight: "-4px" }} />
                  <LeftOutlined style={{ marginRight: "-4px" }} />
                  <LeftOutlined />
                </span>
              </Button>
            </StyledGridCol>
          </StyledGridRow>

          <StyledGridRow gutter={16}>
            {/* Left — read-only state summary */}
            <StyledGridCol lg={12}>
              <div className="result--box">
                <div className="d-flex-item">
                  <div className="key">Warrant Name: </div>
                  {useAlias ? (
                    <FormItem name="bioAliasId">
                      <Select
                        options={aliasOptions}
                        onChange={(value) =>
                          dispatch(patchActiveWarrant({ bioAliasId: value }))
                        }
                      />
                    </FormItem>
                  ) : (
                    isNonEmptyVariable(name) && (
                      <h4 className="title">{name}</h4>
                    )
                  )}
                </div>
                <FormItem
                  label="Use Alias"
                  layout="horizontal"
                  labelPostion="label-right"
                  name="useAlias"
                  valuePropName="checked"
                >
                  <Checkbox
                    onChange={(e) =>
                      dispatch(patchActiveWarrant({ useAlias: e.target.checked }))
                    }
                  />
                </FormItem>
              </div>

              <div className="result--box" style={{ marginTop: "15px" }}>
                <div className="d-flex-item">
                  <div className="key">State Status: </div>
                  <div className="value">{stateInfo.lastStateStatus ?? "-"}</div>
                </div>
                <div className="d-flex-item">
                  <div className="key">State Assigned Number: </div>
                  <div className="value">{stateInfo.stateAssignedNumber ?? "-"}</div>
                </div>
                <div className="d-flex-item">
                  <div className="key">State Date of Warrant: </div>
                  <div className="value">{stateInfo.stateDateOfWarrant ?? "-"}</div>
                </div>
                <div className="d-flex-item">
                  <div className="key">Last Communication: </div>
                  <div className="value">{stateInfo.lastCommunicationWithState ?? "-"}</div>
                </div>
                <div className="d-flex-item">
                  <div className="key">Last Status: </div>
                  <div className="value">
                    {stateInfo.lastCommunicationWithStateStatus ?? "-"}
                  </div>
                </div>
              </div>
            </StyledGridCol>

            {/* Right — editable misc fields */}
            <StyledGridCol lg={12}>
              <FormItem label="User-Entered Misc. Field" name="miscellaneousField">
                <TextField
                  inputType="textarea"
                  className="field--pink"
                  rows="3"
                  onChange={(e) =>
                    patchStateInfoDebounced({ miscellaneousField: e.target.value })
                  }
                />
              </FormItem>

              <FormItem
                label="Automatically Generated Misc. Field"
                name="miscellaneousFieldAuto"
              >
                <TextField inputType="textarea" rows="2" className="field--pink" disabled />
                <div className="count-char">
                  <span>
                    <b>Length:</b> {stateInfo.miscellaneousFieldAuto?.length ?? 0}
                  </span>
                  <span>
                    <b>Remaining:</b>{" "}
                    {500 - (stateInfo.miscellaneousFieldAuto?.length ?? 0)}
                  </span>
                </div>
              </FormItem>
            </StyledGridCol>
          </StyledGridRow>
        </Form>
      </StyledInformationList>

      {isShowSubMission && <StateSubmissions />}
    </>
  );
};

export default StateSubmissionInformation;
